import React, { Suspense } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useAdminAuth } from "../../contexts/AdminAuthContext";
import {
  LayoutDashboard,
  FileText,
  Inbox,
  Mail,
  Users,
  BarChart3,
  Image as ImageIcon,
  Briefcase,
  CreditCard,
  ShoppingBag,
  ShoppingCart,
  GraduationCap,
  Award,
  LogOut,
  TrainFront,
  KeyRound,
  Send,
  Shield,
  Share2,
  Power,
  UserCheck,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/content", label: "Content", icon: FileText },
  { to: "/admin/inbox", label: "Inbox", icon: Inbox },
  { to: "/admin/subscribers", label: "Subscribers", icon: Mail },
  { to: "/admin/users", label: "Free Users", icon: Users },
  { to: "/admin/customers", label: "Customers", icon: ShoppingBag },
  { to: "/admin/products", label: "Product Availability", icon: Power },
  { to: "/admin/abandoned-carts", label: "Abandoned Carts", icon: ShoppingCart },
  { to: "/admin/lifecycle", label: "Lifecycle Emails", icon: Send },
  { to: "/admin/online-assessment", label: "OA Scenario Bank", icon: Shield },
  { to: "/admin/lms", label: "LMS Courses", icon: GraduationCap },
  { to: "/admin/certificates", label: "Certificates", icon: Award },
  { to: "/admin/payments", label: "Payments", icon: CreditCard },
  { to: "/admin/referrals", label: "Referral Programme", icon: Share2 },
  { to: "/admin/interview-coaches", label: "Interview Coaches", icon: UserCheck },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/media", label: "Media", icon: ImageIcon },
  { to: "/admin/jobs", label: "Jobs Cache", icon: Briefcase },
  { to: "/admin/password", label: "Change Password", icon: KeyRound },
];

export default function AdminLayout() {
  const { token, me, loading, logout } = useAdminAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-400 text-sm">Loading admin…</p>
      </div>
    );
  }
  if (!token) {
    return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="w-64 bg-[#0F1B2E] text-slate-200 flex flex-col sticky top-0 h-screen">
        <div className="p-5 border-b border-white/10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
            <TrainFront className="w-5 h-5 text-[#0F1B2E]" strokeWidth={2.4} />
          </div>
          <div>
            <p className="font-extrabold text-white text-sm">TDF Admin</p>
            <p className="text-[10px] uppercase tracking-widest text-slate-400">Control Panel</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-[#E63B2E] text-white" : "hover:bg-white/10 text-slate-300"
                }`
              }
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <p className="text-xs text-slate-400 mb-2 truncate">{me?.email}</p>
          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm bg-white/10 hover:bg-[#E63B2E] transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 p-6 lg:p-10 overflow-x-auto">
        <Suspense fallback={<div className="flex items-center justify-center min-h-[40vh]"><div className="w-10 h-10 border-4 border-slate-700 border-t-[#E63B2E] rounded-full animate-spin" /></div>}>
          <Outlet />
        </Suspense>
      </main>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}
